import { getAddress, isAddress } from "viem";

export function toChecksumAddress(addr: string): string {
  try {
    return isAddress(addr) ? getAddress(addr) : addr;
  } catch {
    return addr;
  }
}

const ADDRESS_KEY_PATTERNS = [
  "accountaddress",
  "account_address",
  "walletaddress",
  "wallet_address",
  "recipientaddress",
  "recipient_address",
  "fromaddress",
  "from_address",
  "toaddress",
  "to_address",
  "owneraddress",
  "owner_address",
  "senderaddress",
  "sender_address",
  "address",
];

export const SOLANA_BASE58_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

export function isSolanaAddress(value: string): boolean {
  return SOLANA_BASE58_REGEX.test(value);
}

/**
 * Normalize a blockchain address for comparison/storage:
 * - EVM: lowercase (case-insensitive per spec, lowercase is canonical)
 * - Solana: unchanged (base58 is case-sensitive)
 */
export function normalizeAddress(address: string): string {
  if (!address) return address;
  // EVM addresses start with 0x — lowercase for canonical form
  if (address.startsWith("0x")) return address.toLowerCase();
  // Solana (base58) and other formats — preserve case
  return address;
}

/**
 * Compare two blockchain addresses for equality.
 * Handles EVM case-insensitivity and Solana case-sensitivity.
 */
export function addressEquals(a: string, b: string): boolean {
  if (!a || !b) return false;
  return normalizeAddress(a) === normalizeAddress(b);
}

export function isAddressField(key: string, value: unknown, excludeKeys: string[] = ["contract", "token"]): boolean {
  if (typeof value !== "string") return false;

  const isEvmAddress = value.startsWith("0x") && value.length === 42;
  const isSolAddress = SOLANA_BASE58_REGEX.test(value);

  if (!isEvmAddress && !isSolAddress) return false;

  const lowerKey = key.toLowerCase();

  for (const exclude of excludeKeys) {
    if (lowerKey.includes(exclude)) return false;
  }

  return ADDRESS_KEY_PATTERNS.some(pattern => lowerKey.includes(pattern));
}
